<template>
  <v-container fluid class="pa-4">
    <v-card elevation="2" rounded="lg">
      <v-card-title class="d-flex align-center justify-space-between flex-wrap ga-3">
        <div>
          <div class="text-h5 font-weight-bold">
            Repartos de hoy
          </div>

          <div class="text-body-2 text-medium-emphasis mt-1">
            Líneas planificadas para la fecha seleccionada.
          </div>
        </div>

        <div class="d-flex ga-2 align-center flex-wrap">
          <v-text-field
            v-model="filters.data"
            type="date"
            label="Fecha"
            variant="outlined"
            density="compact"
            hide-details
            style="min-width: 170px"
          />

          <v-select
            v-model="filters.service_type_id"
            :items="serviceTypes"
            label="Servicio"
            item-title="title"
            item-value="value"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            style="min-width: 220px"
          />

          <v-btn
            color="primary"
            variant="flat"
            :loading="loading"
            prepend-icon="mdi-refresh"
            @click="fetchData"
          >
            Actualizar
          </v-btn>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text>
        <v-alert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          class="mb-4"
        >
          {{ errorMessage }}
        </v-alert>

        <v-alert
          type="info"
          variant="tonal"
          class="mb-4"
        >
          Si el equipo aparece en rojo, significa que una delegación Traycco no tiene equipo final asignado.
        </v-alert>

        <v-row class="mb-4">
          <v-col cols="12" md="3">
            <v-card variant="tonal" rounded="lg">
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Líneas
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ items.length }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card variant="tonal" rounded="lg">
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Folletos
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ totalFolletos.toLocaleString('es-ES') }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card
              variant="tonal"
              rounded="lg"
              :color="totalSinEquipo > 0 ? 'error' : undefined"
            >
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Sin equipo asignado
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ totalSinEquipo }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card
              variant="tonal"
              rounded="lg"
              :color="totalSinPuntoInicio > 0 ? 'warning' : undefined"
            >
              <v-card-text>
                <div class="text-caption text-medium-emphasis">
                  Sin punto inicio
                </div>
                <div class="text-h5 font-weight-bold">
                  {{ totalSinPuntoInicio }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-text-field
          v-model="search"
          label="Buscar por campaña, cliente, proveedor, equipo, zona, tienda o punto inicio"
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          clearable
          hide-details
          class="mb-4"
        />

        <v-data-table
          :headers="headers"
          :items="items"
          :search="search"
          :loading="loading"
          item-value="id"
          density="compact"
          class="elevation-1 rounded-lg"
          :items-per-page="50"
        >
          <template #item.campaign_name="{ item }">
            <div class="font-weight-medium">
              {{ item.campaign_name }}
            </div>
          </template>

          <template #item.customer_name="{ item }">
            <div>
              {{ item.customer_name }}
            </div>
          </template>

          <template #item.partner_sort="{ item }">
            <div class="font-weight-medium">
              {{ item.partner_name || 'Sin proveedor' }}
            </div>
          </template>

          <template #item.team_sort="{ item }">
            <div
              :class="item.needs_team_assignment ? 'text-error font-weight-bold' : ''"
            >
              {{ item.team_name || '-' }}
            </div>

            <div
              v-if="item.needs_team_assignment"
              class="text-caption text-error"
            >
              Falta asignar equipo
            </div>
          </template>

          <template #item.zone_name="{ item }">
            <div class="text-right">
              {{ item.zone_name }}
            </div>
          </template>

          <template #item.zone_city="{ item }">
            <div>
              {{ item.zone_city }}
              <span v-if="item.zone_reference">
                , {{ item.zone_reference }}
              </span>
            </div>
          </template>

          <template #item.dates_sort="{ item }">
            <div class="text-caption">
              <div>Inicio: {{ formatDate(item.start_date) }}</div>
              <div>Fin: {{ formatDate(item.end_date) }}</div>

              <div
                v-if="item.distribute_date"
                class="text-success font-weight-medium"
              >
                Reparto: {{ formatDate(item.distribute_date) }}
              </div>

              <div
                v-if="item.planning_date"
                class="text-primary"
              >
                Plan.: {{ formatDateTime(item.planning_date) }}
              </div>
            </div>
          </template>

          <template #item.quantity="{ item }">
            <div class="text-right font-weight-medium">
              {{ Number(item.quantity || 0).toLocaleString('es-ES') }}
            </div>
          </template>

          <template #item.punt_inici_sort="{ item }">
            <div
              :class="!item.punt_inici ? 'text-warning font-weight-bold' : ''"
            >
              {{ item.punt_inici || 'Sin punto inicio' }}
            </div>
          </template>

          <template #item.hora_inici="{ item }">
            <div>
              {{ item.hora_inici || '-' }}
            </div>
          </template>

          <template #item.shop="{ item }">
            <div>
              {{ item.shop || '-' }}
            </div>
          </template>

          <template #no-data>
            <div class="pa-6 text-center text-medium-emphasis">
              No hay repartos para la fecha seleccionada.
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'

const loading = ref(false)
const errorMessage = ref('')
const search = ref('')
const items = ref([])

const today = new Date().toISOString().slice(0, 10)

const filters = ref({
  data: today,
  service_type_id: null,
})

const serviceTypes = [
  { title: 'Todos los servicios', value: null },
  { title: 'Buzoneo masivo', value: 1 },
  { title: 'Entrega en tienda', value: 5 },
]

const headers = [
  { title: 'Campaña', key: 'campaign_name', sortable: true },
  { title: 'Cliente', key: 'customer_name', sortable: true },
  { title: 'Proveedor', key: 'partner_sort', sortable: true },
  { title: 'Equipo', key: 'team_sort', sortable: true },
  { title: 'Zona Traycco', key: 'zone_name', sortable: true },
  { title: 'Población', key: 'zone_city', sortable: true },
  { title: 'Fechas', key: 'dates_sort', sortable: true },
  { title: 'Folletos', key: 'quantity', sortable: true, align: 'end' },
  { title: 'Ref. folleto', key: 'flyer_reference', sortable: true },
  { title: 'Punto inicio', key: 'punt_inici_sort', sortable: true },
  { title: 'Hora inicio', key: 'hora_inici', sortable: true },
  { title: 'Tienda', key: 'shop', sortable: true },
]

const totalFolletos = computed(() => {
  return items.value.reduce((total, item) => total + Number(item.quantity || 0), 0)
})

const totalSinEquipo = computed(() => {
  return items.value.filter(item => item.needs_team_assignment).length
})

const totalSinPuntoInicio = computed(() => {
  return items.value.filter(item => !item.punt_inici).length
})

async function fetchData() {
  loading.value = true
  errorMessage.value = ''

  try {
    const params = {
      data: filters.value.data,
    }

    if (filters.value.service_type_id) {
      params.service_type_id = filters.value.service_type_id
    }

    const response = await axios.get('/api/admin/produccion/repartos-hoy', {
      params,
    })

    items.value = (response.data?.data || []).map(item => ({
      ...item,

      partner_sort: item.partner_name || '',
      team_sort: item.team_name || '',
      punt_inici_sort: item.punt_inici || '',
      dates_sort: item.distribute_date || item.end_date || item.start_date || '',

      searchable_text: [
        item.campaign_name,
        item.customer_name,
        item.partner_name,
        item.team_name,
        item.zone_name,
        item.zone_city,
        item.zone_reference,
        item.flyer_reference,
        item.punt_inici,
        item.hora_inici,
        item.shop,
        item.jefe_equipo,
        item.telefono,
      ].filter(Boolean).join(' '),
    }))
  } catch (error) {
    console.error(error)

    errorMessage.value =
      error.response?.data?.message ||
      'No se ha podido cargar el listado de repartos de hoy.'
  } finally {
    loading.value = false
  }
}

function formatDate(date) {
  if (!date) return '-'

  const cleanDate = String(date).slice(0, 10)
  const parts = cleanDate.split('-')

  if (parts.length !== 3) {
    return date
  }

  return `${parts[2]}/${parts[1]}/${parts[0]}`
}

function formatDateTime(date) {
  if (!date) return '-'

  const text = String(date).replace('T', ' ')
  const [datePart, timePart = ''] = text.split(' ')

  const formattedDate = formatDate(datePart)
  const formattedTime = timePart.slice(0, 5)

  return formattedTime ? `${formattedDate} ${formattedTime}` : formattedDate
}

onMounted(() => {
  fetchData()
})
</script>